/**
 * ContentHelper
 * Utility functions for DOM manipulation used by content strategies
 */

import type { PeriodType, TabName } from '@/types';
import { STORAGE_KEYS } from '@/utils/constants';

/**
 * Show multiple DOM elements
 */
export function showElements(selectors: string[]): void {
  selectors.forEach((selector) => {
    const element = document.querySelector(selector);
    if (element && element instanceof HTMLElement) {
      element.style.display = '';
    }
  });
}

/**
 * Hide multiple DOM elements
 */
export function hideElements(selectors: string[]): void {
  selectors.forEach((selector) => {
    const element = document.querySelector(selector);
    if (element && element instanceof HTMLElement) {
      element.style.display = 'none';
    }
  });
}

/**
 * Create HTML for image display
 * @param imagePaths - Array of image paths
 * @param containerClass - CSS class for the container div
 * @param styles - Optional inline styles as key-value pairs
 * @param imageWidth - Optional custom width (single string for all images or array for each image)
 * @returns HTML string
 */
export function createImageHTML(
  imagePaths: readonly string[],
  containerClass: string,
  styles?: Record<string, string>,
  imageWidth?: string | string[]
): string {
  const defaultStyles = {
    'margin-top': '10px',
    'margin-left': '20px',
    'padding-bottom': '20px',
  };

  const combinedStyles = { ...defaultStyles, ...styles };
  const styleString = Object.entries(combinedStyles)
    .map(([key, value]) => `${key}: ${value}`)
    .join('; ');

  const imagesHTML = imagePaths
    .map((path, index) => {
      // Determine width for this image
      let width = '92%'; // default
      if (typeof imageWidth === 'string') {
        width = imageWidth;
      } else if (Array.isArray(imageWidth) && imageWidth[index] !== undefined && imageWidth[index] !== null && imageWidth[index] !== '') {
        width = imageWidth[index];
      }

      return `<img src="${path}" alt="${containerClass}" style="max-width: ${width}; height: auto;" onerror="this.src='/img/placeholder.png'">`;
    })
    .join('\n      ');

  return `
    <div class="${containerClass}" style="${styleString}">
      ${imagesHTML}
    </div>
  `;
}

/**
 * Create HTML for quarterly income statement images (custom styling)
 */
export function createQuarterlyIncomeImageHTML(
  imagePaths: readonly string[],
  containerClass: string
): string {
  if (imagePaths.length !== 2) {
    // Fallback to standard layout
    return createImageHTML(imagePaths, containerClass);
  }

  return `
    <div class="${containerClass}" style="margin-top: 10px; padding-bottom: 20px; display: flex; flex-direction: row; align-items: flex-start;">
      <img src="${imagePaths[0]}" alt="${containerClass} (Part 1)" style="max-width: 126%; height: auto; display: block; padding-left: 10px; padding-top: 5px;">
      <img src="${imagePaths[1]}" alt="${containerClass} (Part 2)" style="max-width: 104%; height: auto; display: block; margin-left: 0; padding-left: 0; padding-right: 5px; padding-top: 5px;">
    </div>
  `;
}

/**
 * Inject HTML content into a target element (null-safe)
 * @param selector - CSS selector for target element
 * @param html - HTML string to inject
 * @returns true if injection was successful, false if element not found
 */
export function injectContent(selector: string, html: string): boolean {
  const element = document.querySelector(selector);
  if (element) {
    element.innerHTML = html;
    return true;
  }
  console.warn(`[ContentHelper] Element not found for selector: ${selector}`);
  return false;
}

/**
 * Remove existing sections by class name
 */
export function removeSections(selectors: string[]): void {
  selectors.forEach((selector) => {
    const elements = document.querySelectorAll(selector);
    elements.forEach((el) => el.remove());
  });
}

/**
 * Replace table HTML and return the original for restoration
 * @param containerSelector - CSS selector for table container
 * @param newTableHTML - New table HTML
 * @returns Original table HTML (or empty string if container not found)
 */
export function replaceTableHTML(
  containerSelector: string,
  newTableHTML: string
): string {
  const tableContainer = document.querySelector(
    containerSelector
  );

  if (!tableContainer || !(tableContainer instanceof HTMLElement)) return '';

  const originalHTML = tableContainer.innerHTML;

  // Modify container styles
  tableContainer.style.border = 'none';
  tableContainer.style.width = '60%';

  // Replace content
  tableContainer.innerHTML = newTableHTML;

  return originalHTML;
}

/**
 * Restore original table HTML
 * @param containerSelector - CSS selector for table container
 * @param originalHTML - Original HTML to restore
 */
export function restoreTableHTML(
  containerSelector: string,
  originalHTML: string
): void {
  const tableContainer = document.querySelector(
    containerSelector
  );

  if (!tableContainer || !(tableContainer instanceof HTMLElement) || !originalHTML) return;

  // Reset container styles
  tableContainer.style.border = '';
  tableContainer.style.width = '';

  // Restore content
  tableContainer.innerHTML = originalHTML;
}

/**
 * Get current period type from localStorage or dropdown
 */
export function getCurrentPeriodType(): PeriodType {
  // Try localStorage first
  try {
    const storedPeriodType = localStorage.getItem(STORAGE_KEYS.PERIOD_TYPE);
    if (storedPeriodType !== null && storedPeriodType !== '') {
      return storedPeriodType as PeriodType;
    }
  } catch (e) {
    console.error('Error reading period type from localStorage:', e);
  }

  // Fallback to dropdown
  const periodTypeDropdown = document.querySelector(
    '.control-select'
  );

  if (
    periodTypeDropdown &&
    periodTypeDropdown.previousElementSibling &&
    periodTypeDropdown.previousElementSibling.textContent?.includes(
      'Period Type:'
    )
  ) {
    return periodTypeDropdown.value as PeriodType;
  }

  // Default fallback
  return 'Annual';
}

/**
 * Get current tab from pathname or active tab element
 */
export function getCurrentTab(): TabName {
  const pathname = window.location.pathname;

  // Check pathname first
  if (
    pathname.includes('nvidia-income-statement.html') ||
    pathname.includes('income-statement')
  ) {
    return 'income-statement';
  }

  if (
    pathname.includes('nvidia-balance-sheet.html') ||
    pathname.includes('balance-sheet')
  ) {
    return 'balance-sheet';
  }

  if (
    pathname.includes('nvidia-cash-flow.html') ||
    pathname.includes('cash-flow')
  ) {
    return 'cash-flow';
  }

  // Check active tab text
  const activeTab = document.querySelector('.tab.active');
  if (activeTab) {
    const tabText = activeTab.textContent?.toLowerCase() || '';

    if (tabText.includes('income statement')) {
      return 'income-statement';
    }

    if (tabText.includes('balance sheet')) {
      return 'balance-sheet';
    }

    if (tabText.includes('cash flow')) {
      return 'cash-flow';
    }
  }

  // Default to key-stats
  return 'key-stats';
}

/**
 * Clear the main section content (null-safe)
 * @returns true if clearing was successful, false if element not found
 */
export function clearMainSection(): boolean {
  const mainSection = document.querySelector('.main-section');
  if (mainSection) {
    mainSection.innerHTML = '';
    return true;
  }
  console.warn('[ContentHelper] .main-section not found for clearing');
  return false;
}

/**
 * Safely inject content and hide elements in a single operation
 * Ensures atomic DOM updates for Key Stats tab
 * @param targetSelector - Where to inject content
 * @param html - HTML to inject
 * @param hideSelectors - Elements to hide
 * @param showSelectors - Elements to show
 */
export function safelyUpdateContent(
  targetSelector: string,
  html: string,
  hideSelectors: string[] = [],
  showSelectors: string[] = []
): void {
  // First, hide elements that need to be hidden
  hideElements(hideSelectors);

  // Then, show elements that need to be shown
  showElements(showSelectors);

  // Finally, inject the content
  const success = injectContent(targetSelector, html);

  if (!success) {
    console.error(`[ContentHelper] Failed to inject content into ${targetSelector}`);
  }
}

/**
 * Key Stats content sections that should be hidden/shown
 */
const KEY_STATS_SECTIONS = [
  '.key-financials-wrapper',
  '.financials-table-container',
  '.footnote',
  '.capitalization-section',
  '.valuation-section'
];

/**
 * Hide original Key Stats content (for when showing "No data" message)
 */
export function hideKeyStatsContent(): void {
  hideElements(KEY_STATS_SECTIONS);
}

/**
 * Show original Key Stats content (restore visibility)
 */
export function showKeyStatsContent(): void {
  showElements(KEY_STATS_SECTIONS);
}

/**
 * Hide the "No data available" message (set display: none)
 */
export function hideNoDataMessage(): void {
  const message = document.querySelector('.no-data-message');
  if (message instanceof HTMLElement) {
    message.style.display = 'none';
  }
}

/**
 * Show the "No data available" message (set display: block)
 */
export function showNoDataMessage(): void {
  const message = document.querySelector('.no-data-message');
  if (message instanceof HTMLElement) {
    message.style.display = 'block';
  }
}
